import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Divider,
  IconButton,
  Collapse,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Pets as PetsIcon,
  LocalHospital as HospitalIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { SearchResult } from '../../services/api';

interface SearchResultsPanelProps {
  results: SearchResult[];
  maxHeight?: string;
}

const SearchResultsPanel: React.FC<SearchResultsPanelProps> = ({ 
  results, 
  maxHeight = '400px' 
}) => {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [showPanel, setShowPanel] = useState(true);

  const handleCardExpand = (caseId: string) => {
    setExpandedCard(expandedCard === caseId ? null : caseId);
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'success.main';
    if (score >= 0.6) return 'warning.main';
    return 'error.main';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 0.8) return 'High Match';
    if (score >= 0.6) return 'Medium Match';
    return 'Low Match';
  };

  const formatSymptoms = (symptoms: string[] | string) => {
    if (Array.isArray(symptoms)) {
      return symptoms.filter(s => s && s.toLowerCase() !== 'unknown');
    }
    return typeof symptoms === 'string' && symptoms.toLowerCase() !== 'unknown' ? [symptoms] : [];
  };

  if (results.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper 
        elevation={3} 
        sx={{ 
          m: 2, 
          bgcolor: 'background.paper',
          borderLeft: 4,
          borderColor: '#22537f',
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: '#22537f', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon />
            <Typography variant="h6">
              Similar Cases Found ({results.length})
            </Typography>
          </Box>
          <IconButton 
            color="inherit" 
            onClick={() => setShowPanel(!showPanel)}
            size="small"
          >
            {showPanel ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Box>

        <Collapse in={showPanel}>
          <Box 
            sx={{ 
              maxHeight, 
              overflow: 'auto',
              p: 2,
            }}
          >
                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
               {results.map((result, index) => (
                 <Box key={result.id || index} sx={{ flex: '1 1 400px', minWidth: 0 }}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        elevation: 4,
                        transform: 'translateY(-2px)',
                      },
                      border: expandedCard === result.id ? 2 : 0,
                      borderColor: 'primary.main',
                    }}
                    onClick={() => handleCardExpand(result.id)}
                  >
                    <CardContent>
                      {/* Case Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                          {result.metadata?.case_number || result.case_number || `Case ${index + 1}`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={getScoreLabel(result.score)}
                            size="small"
                            sx={{ 
                              bgcolor: getScoreColor(result.score),
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {(result.score * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>

                      {/* Score Bar */}
                      <Box sx={{ mb: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={result.score * 100}
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getScoreColor(result.score),
                            },
                          }}
                        />
                      </Box>

                      {/* Species */}
                      {result.species && result.species.toLowerCase() !== 'unknown' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PetsIcon fontSize="small" color="primary" />
                          <Typography variant="body2">
                            <strong>Species:</strong> {result.species}
                          </Typography>
                        </Box>
                      )}

                      {/* Symptoms */}
                      {formatSymptoms(result.symptoms).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Symptoms:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {formatSymptoms(result.symptoms).slice(0, 3).map((symptom, idx) => (
                              <Chip
                                key={idx}
                                label={symptom}
                                size="small"
                                variant="outlined"
                                color="secondary"
                              />
                            ))}
                            {formatSymptoms(result.symptoms).length > 3 && (
                              <Chip
                                label={`+${formatSymptoms(result.symptoms).length - 3} more`}
                                size="small"
                                variant="outlined"
                                color="default"
                              />
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Quick Info */}
                                             <Box>
                         {result.diagnosis && result.diagnosis.toLowerCase() !== 'unknown' && (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                             <HospitalIcon fontSize="small" color="success" />
                             <Typography variant="body2" noWrap>
                               <strong>Diagnosis:</strong> {result.diagnosis}
                             </Typography>
                           </Box>
                         )}
                         {result.outcome && result.outcome.toLowerCase() !== 'unknown' && (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             <TrendingUpIcon fontSize="small" color="info" />
                             <Typography variant="body2" noWrap>
                               <strong>Outcome:</strong> {result.outcome}
                             </Typography>
                           </Box>
                         )}
                       </Box>

                      {/* Expanded Content */}
                      <Collapse in={expandedCard === result.id}>
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ mb: 2 }} />
                          
                          {/* Full Case Text */}
                          {result.full_case_text && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="primary" gutterBottom>
                                Full Case Description:
                              </Typography>
                              <Paper 
                                variant="outlined" 
                                sx={{ 
                                  p: 2, 
                                  bgcolor: 'grey.50',
                                  maxHeight: '200px',
                                  overflow: 'auto',
                                }}
                              >
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {result.full_case_text}
                                </Typography>
                              </Paper>
                            </Box>
                          )}

                          {/* Treatment */}
                          {result.treatment && result.treatment.toLowerCase() !== 'unknown' && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="primary" gutterBottom>
                                Treatment:
                              </Typography>
                              <Typography variant="body2">
                                {result.treatment}
                              </Typography>
                            </Box>
                          )}

                          {/* Additional Metadata */}
                          {result.metadata && Object.keys(result.metadata).length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" color="primary" gutterBottom>
                                Additional Information:
                              </Typography>
                                                             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                 {Object.entries(result.metadata).map(([key, value]) => 
                                   value && value !== 'unknown' ? (
                                     <Chip
                                       key={key}
                                       label={`${key}: ${value}`}
                                       size="small"
                                       variant="outlined"
                                     />
                                   ) : null
                                 )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Collapse>

                      {/* Expand Button */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardExpand(result.id);
                          }}
                          startIcon={
                            <ExpandMoreIcon 
                              sx={{ 
                                transform: expandedCard === result.id ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s',
                              }} 
                            />
                          }
                        >
                          {expandedCard === result.id ? 'Show Less' : 'Show More'}
                        </Button>
                      </Box>
                                         </CardContent>
                   </Card>
                 </Box>
               ))}
             </Box>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default SearchResultsPanel; 